import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "WedgeWireTubes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Screen Tubes",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Continuous Slot Wedge Wire Tubes & Candle Elements",
      lead: "All-welded continuous-slot screen tubes and tubular candle elements engineered for high-pressure separation, catalyst retention, and severe solid-liquid straining across pressurized industrial vessels.",
      secondaryCta: {
        label: "Screen Tubes Category",
        to: "/ScreenTubesPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
        alt: "Industrial stainless steel wedge wire candle filter element for pressurized vessels",
      },
      cardNote: "High-Pressure Candle Media",
      cardLink: {
        label: "Technical Specs",
        to: "/WedgeWireTubesPage#specifications",
      },
      specs: [
        { label: "Slot Openings", value: "0.02 – 5.0 mm" },
        { label: "Standard Materials", value: "AISI 304 / 316L" },
        { label: "Standard Lengths", value: "500 – 3,000 mm" },
        { label: "End Connections", value: "Thread / Flange / Weld" },
      ],
    },
    overview: {
      eyebrow: "Catalyst Retention & Pressure Filtration",
      heading: "Rigid All-Welded Candle Construction for High Differential Pressures",
      paragraphs: [
        "Wedge wire tubes and candle elements are precision-welded cylindrical filters designed for vertical pressure leaf and candle filter vessels. Fabricated with continuous external or internal V-wire wraps over longitudinal support bars, they deliver rigid monolithic strength without internal core degradation.",
        "In catalyst recovery, activated carbon retention, and fine chemical polishing, conventional wire cloth or textile socks risk tear-out, fiber migration, and blinding. Wedge wire candle tubes eliminate media migration, resisting intense backpulse shocks and cyclic reverse flushing without deformation.",
        "Available in standardized outer diameters matching multi-element tube sheets (commonly 25 mm to 150 mm) and custom dimensions up to 600 mm, each tube is configured with threaded fittings (BSP/NPT), quick-release bayonets, welded flange adaptors, or blind bottom plugs machined to high dimensional tolerances.",
      ],
      keyPoints: [
        "Rigid all-welded V-wire structure withstands high forward and reverse pressure differentials without wire shifting.",
        "Inward-widening slot aperture eliminates particle entrapment, enabling rapid cake discharge and low backwash volume.",
        "Manufactured in AISI 304 and 316L stainless steel for full compatibility with aggressive solvents, acids, and thermal regeneration.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
        alt: "Industrial stainless steel wedge wire candle filter element for pressurized vessels",
      },
    },
    specTable: {
      heading: "Wedge Wire Tube & Candle Element Specifications",
      intro: "Technical parameters for pressurized candle filter elements and tubular screens. Custom diameters, thread standards, and lengths are engineered to order.",
      columns: [
        { key: "parameter", label: "Parameter", align: "left" },
        { key: "range", label: "Manufacturing Range", align: "left" },
        { key: "tolerance", label: "Dimensional Tolerance", align: "left" },
        { key: "notes", label: "Engineering Details", align: "left" },
      ],
      rows: [
        {
          id: "wwt-slot",
          parameter: "Slot Width Range",
          range: "0.02 – 5.0 mm",
          tolerance: "±0.015 mm",
          notes: "Consistent micron rating across active filtering area",
        },
        {
          id: "wwt-od",
          parameter: "Outside Diameter (OD)",
          range: "19 – 300 mm",
          tolerance: "±0.3 mm",
          notes: "Standard candle vessel sizes: 25, 33, 38, 50, 70 mm OD",
        },
        {
          id: "wwt-length",
          parameter: "Standard Lengths",
          range: "250 – 3,000 mm",
          tolerance: "±1.0 mm",
          notes: "Custom multi-meter assemblies built upon specification",
        },
        {
          id: "wwt-alloy",
          parameter: "Stainless Steel Grades",
          range: "AISI 304, AISI 316L",
          tolerance: "Standard chemistry",
          notes: "Certified material test reports available on request",
        },
        {
          id: "wwt-fittings",
          parameter: "Mounting Connections",
          range: "Male/Female NPT, BSP, Sanitary Flange, Tie-Rod",
          tolerance: "Machined to specification",
          notes: "Supplied with machined O-ring grooves or flat gasket seats",
        },
      ],
      caption: "Values represent verified engineering ranges. Additional configurations engineered to specification.",
    },
    features: {
      heading: "Engineering & Performance Advantages",
      intro: "Purpose-built for demanding vertical pressure vessels, automated backwashing systems, and severe industrial straining.",
      items: [
        {
          title: "Zero Fiber or Media Migration",
          text: "All-metal stainless construction ensures absolute fluid purity without shed fibers, polymer breakdown, or seam unraveling.",
        },
        {
          title: "Dynamic Backwash Release",
          text: "Continuous narrow outer slot expands inward, releasing filter cake cleanly and rapidly during pneumatic gas bump or liquid reverse pulse.",
        },
        {
          title: "High Forward & Reverse Differential",
          text: "Resistance-welded joints at every support bar intersection deliver high burst and collapse resistance against sudden pressure surges.",
        },
        {
          title: "Precision Tube Sheet Seating",
          text: "Machined top connectors ensure accurate perpendicular alignment across dense multi-candle tube sheet arrays.",
        },
      ],
    },
    related: {
      heading: "Related Cylindrical Wedge Wire Products",
      intro: "Explore continuous-slot screen pipes or navigate back to the parent screen tubes engineering hub.",
      cards: [
        {
          title: "Screen Pipes & Lateral Hubs",
          text: "Continuous V-profile wire welded to longitudinal support rods for deep intake wells, media underdrains, and uniform fluid distribution.",
          to: "/ScreenPipesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
            alt: "Stainless steel wedge wire screen pipe with continuous slot and support rods",
          },
          badge: "Well Intake & Drains",
          linkLabel: "View Screen Pipes",
        },
        {
          title: "Screen Tubes Category Hub",
          text: "Precision cylindrical screen tubes and all-welded tube assemblies designed for severe differential pressure and high collapse resistance.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Stainless steel wire mesh screen tube cylinder",
          },
          badge: "Parent Category",
          linkLabel: "Back to Screen Tubes",
        },
        {
          title: "Wedge Wire Screen Overview",
          text: "Complete line of flat panels, curved screens, slot cylinders, and specialized wedge wire assemblies for heavy industry.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Overview of stainless steel wedge wire screens",
          },
          badge: "Wedge Wire Family",
          linkLabel: "Explore Wedge Wire",
        },
      ],
    },
    cta: {
      id: "wedge-wire-tubes-quote",
      eyebrow: "Custom Candle Element Manufacturing",
      heading: "Request a Technical Quote for Wedge Wire Tubes",
      lead: "Submit your slot rating, candle outer diameter, active length, vessel tube sheet connection, and operating parameters for an engineered technical proposal.",
      secondaryLabel: "Browse Screen Tubes",
      secondaryTo: "/ScreenTubesPage",
      note: "Material test reports (MTR) and positive material identification (PMI) available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "WedgeWireTubes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "筛管与滤筒",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "全焊式楔形丝烛式滤芯与过滤管",
      lead: "全焊式连续精密缝隙楔形丝滤芯与筛管，专为高压固液分离、贵重催化剂回收、精细化学品纯化及工业承压密闭容器过滤而量身设计。",
      secondaryCta: {
        label: "返回筛管分类",
        to: "/ScreenTubesPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
        alt: "用于承压容器的不锈钢全焊式楔形丝烛式滤芯与过滤管",
      },
      cardNote: "承压容器烛式滤元",
      cardLink: {
        label: "技术规格",
        to: "/WedgeWireTubesPage#specifications",
      },
      specs: [
        { label: "缝隙精度", value: "0.02 – 5.0 mm" },
        { label: "主要材质", value: "304L / 316L" },
        { label: "标准长度", value: "500 – 3,000 mm" },
        { label: "端部连接", value: "螺纹 / 法兰 / 焊接" },
      ],
    },
    overview: {
      eyebrow: "催化剂回收与承压密闭过滤",
      heading: "高刚性全焊式烛式结构，适应严苛正反向操作压差",
      paragraphs: [
        "互拓制造的楔形丝烛式滤芯是专为立式密闭烛式过滤机及精密加压叶滤机开发的高端滤元。表面采用高精度 V 型截面丝，经数控设备连续精密熔焊于内部纵向支撑筋条上，形成自支撑的高强度整体结构，无需额外填充骨架。",
        "在贵金属催化剂截留、活性炭脱色脱碳及精细化工流体澄清洗涤中，传统滤布或纺织滤袋极易破损、析出纤维或出现堵塞穿滤。楔形丝滤管具有完全刚性的全金属结构，可耐受高频气压反吹或液体逆流脉冲冲洗，反复再生而不变形。",
        "产品标准外径全面适配主流多孔花板孔径（常用外径 25 mm、33 mm、38 mm、50 mm 及 70 mm 等），单根长度可达 3,000 mm。顶端接口可根据安装要求精密加工快装螺纹、卡扣式接头、平垫密封法兰或盲底密封端盖，密封平整，杜绝介质旁漏。",
      ],
      keyPoints: [
        "全金属整体熔焊结构，耐受高正压过滤与反压反吹，彻底消除丝网位移与纤维脱落污染。",
        "由外向内发散的 V 型微米级缝隙设计，杜绝杂质颗粒卡死，反洗脱饼迅速彻底且耗水量低。",
        "选用优质 AISI 304 及 316L 奥氏体不锈钢材料，耐受强酸强碱、有机溶剂侵蚀及蒸汽在线灭菌。",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
        alt: "用于承压容器的不锈钢全焊式楔形丝烛式滤芯与过滤管",
      },
    },
    specTable: {
      heading: "楔形丝滤管与烛式滤芯技术参数",
      intro: "承压密闭烛式过滤器专用滤芯技术制造规范。支持非标管径、特殊螺纹接口及多节组合设计按图生产。",
      columns: [
        { key: "parameter", label: "参数项目", align: "left" },
        { key: "range", label: "制造加工范围", align: "left" },
        { key: "tolerance", label: "加工公差", align: "left" },
        { key: "notes", label: "工程说明", align: "left" },
      ],
      rows: [
        {
          id: "wwt-slot",
          parameter: "缝隙宽度精度",
          range: "0.02 – 5.0 mm",
          tolerance: "±0.015 mm",
          notes: "全长工作段缝隙均一，截留精度可控",
        },
        {
          id: "wwt-od",
          parameter: "圆筒外径 (OD)",
          range: "19 – 300 mm",
          tolerance: "±0.3 mm",
          notes: "常用烛式标准外径：25、33、38、50、70 mm",
        },
        {
          id: "wwt-length",
          parameter: "滤元标准长度",
          range: "250 – 3,000 mm",
          tolerance: "±1.0 mm",
          notes: "可定制超长规格或多段法兰组合连接",
        },
        {
          id: "wwt-alloy",
          parameter: "材质牌号",
          range: "AISI 304, AISI 316L",
          tolerance: "标准成分",
          notes: "按订单要求可提供材质证明报告 (MTR)",
        },
        {
          id: "wwt-fittings",
          parameter: "固定安装接口",
          range: "公/母管螺纹 (NPT/G/R)、快装法兰、拉杆端盖",
          tolerance: "精密数控加工",
          notes: "端头加工 O 型密封圈槽或平垫密封面",
        },
      ],
      caption: "所列数据为确认的制造能力范围。如有特殊非标要求，支持按图定制加工。",
    },
    features: {
      heading: "工程结构特点与应用优势",
      intro: "专为立式密闭加压容器、自动化脉冲反吹系统及苛刻工业过滤设计。",
      items: [
        {
          title: "零介质脱落与零纤维污染",
          text: "纯金属全焊式结构避免了合成纤维降解与缝线脱落，确保滤后成品高洁净度。",
        },
        {
          title: "极佳的气脉冲反吹脱饼性能",
          text: "外表面狭窄缝隙向内部逐渐开阔，气压反吹或液体反洗时滤饼迅速整片脱落，清洗周期短。",
        },
        {
          title: "耐受双向严苛压差冲击",
          text: "筋条与型材丝每个节点经全熔透电阻焊接，耐受正向高压滤饼负荷与瞬态反吹冲击。",
        },
        {
          title: "高精度花板垂直对中安装",
          text: "数控精车端部连接接头，确保滤元在密集花板矩阵排列时保持高垂直度，防止运行震动触碰。",
        },
      ],
    },
    related: {
      heading: "相关圆柱形楔形丝产品",
      intro: "了解用于水井取水与集水布水的滤水筛管，或返回筛管分类总览查看全系列产品。",
      cards: [
        {
          title: "滤水筛管与集配水管",
          text: "连续 V 型截面丝全焊于纵向支撑筋条结构，为深水取水井、树脂截留及压力容器提供可靠介质截留与均匀流体分布。",
          to: "/ScreenPipesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
            alt: "不锈钢锲形丝连续缝隙滤水筛管与支撑筋条结构",
          },
          badge: "深井与集配水",
          linkLabel: "查看滤水筛管",
        },
        {
          title: "精密筛管与滤筒分类中心",
          text: "采用定制加工不锈钢楔形截面圆筒结构，适用于强腐蚀性流体与高压工况过滤的筛管产品矩阵。",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "不锈钢编织及烧结丝网精密滤筒筛管",
          },
          badge: "上一级分类",
          linkLabel: "返回筛管分类",
        },
        {
          title: "楔形丝筛网总览",
          text: "平面筛板、弧形筛面、圆柱筛筒以及工业滤帽等全系列全焊式楔形丝过滤产品总览。",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "楔形丝筛网产品总览",
          },
          badge: "产品族总览",
          linkLabel: "查看楔形丝总览",
        },
      ],
    },
    cta: {
      id: "wedge-wire-tubes-quote",
      eyebrow: "烛式滤元非标定制",
      heading: "获取楔形丝烛式滤芯技术方案及报价",
      lead: "请提供所需的过滤精度、外径尺寸、工作长度、花板安装接口及工作压差条件。我们的工程师将为您出具详细图纸与技术方案。",
      secondaryLabel: "返回筛管产品中心",
      secondaryTo: "/ScreenTubesPage",
      note: "按订单要求可提供材质检验报告 (MTR) 与 PMI 合金成分分析报告。",
    },
  },
  es: {
    breadcrumbKey: "WedgeWireTubes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Tubos de Filtración",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tubos de Alambre Cuña y Elementos Tipo Bujía",
      lead: "Tubos de ranura continua y elementos tipo bujía totalmente soldados para separación a alta presión, retención de catalizadores y cribado sólido-líquido en recipientes industriales.",
      secondaryCta: {
        label: "Categoría Tubos Filtrantes",
        to: "/ScreenTubesPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
        alt: "Elemento filtrante de tubo de alambre cuña de acero inoxidable para recipientes a presión",
      },
      cardNote: "Elementos Bujía para Alta Presión",
      cardLink: {
        label: "Datos Técnicos",
        to: "/WedgeWireTubesPage#specifications",
      },
      specs: [
        { label: "Aberturas", value: "0,02 – 5,0 mm" },
        { label: "Aleaciones", value: "304L / 316L" },
        { label: "Longitudes", value: "500 – 3.000 mm" },
        { label: "Conexiones", value: "Rosca / Brida / Soldadura" },
      ],
    },
    overview: {
      eyebrow: "Retención de Catalizadores y Filtración a Presión",
      heading: "Construcción Totalmente Soldada para Altas Presiones Diferenciales",
      paragraphs: [
        "Los tubos de alambre en cuña y elementos tipo bujía son filtros cilíndricos de precisión diseñados para recipientes verticales a presión. Fabricados mediante soldadura continua de hilo en V sobre varillas longitudinales, ofrecen gran rigidez sin degradación interna.",
        "En la recuperación de catalizadores y clarificación química, las telas convencionales corren el riesgo de desgarro y migración de fibras. Los elementos de alambre en cuña resisten intensas pulsaciones de retrolavado sin experimentar deformaciones.",
        "Disponibles en diámetros que coinciden con las placas portavelas habituales (de 25 mm a 150 mm) y configuraciones a medida de hasta 600 mm, cuentan con roscas torneadas, acoples rápidos o bridas para asegurar estanqueidad total.",
      ],
      keyPoints: [
        "Estructura en hilo en V totalmente soldada que soporta altas presiones diferenciales directas e inversas.",
        "Abertura ensanchada hacia el interior que evita la retención de partículas y agiliza la descarga de la torta.",
        "Fabricados en acero inoxidable AISI 304 y 316L, aptos para disolventes agresivos y regeneración térmica.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
        alt: "Elemento filtrante de tubo de alambre cuña de acero inoxidable para recipientes a presión",
      },
    },
    specTable: {
      heading: "Especificaciones de Tubos Tipo Bujía",
      intro: "Parámetros técnicos de fabricación para elementos tipo bujía en recipientes a presión. Longitudes y roscas especiales realizadas bajo plano.",
      columns: [
        { key: "parameter", label: "Parámetro", align: "left" },
        { key: "range", label: "Rango Estándar", align: "left" },
        { key: "tolerance", label: "Tolerancia", align: "left" },
        { key: "notes", label: "Detalles Técnicos", align: "left" },
      ],
      rows: [
        {
          id: "wwt-slot",
          parameter: "Abertura de ranura",
          range: "0,02 – 5,0 mm",
          tolerance: "±0,015 mm",
          notes: "Uniformidad estricta en toda la superficie filtrante",
        },
        {
          id: "wwt-od",
          parameter: "Diámetro exterior (OD)",
          range: "19 – 300 mm",
          tolerance: "±0,3 mm",
          notes: "Tamaños estándar de bujía: 25, 33, 38, 50, 70 mm",
        },
        {
          id: "wwt-length",
          parameter: "Longitud estándar",
          range: "250 – 3.000 mm",
          tolerance: "±1,0 mm",
          notes: "Posibilidad de longitudes mayores bajo especificación",
        },
        {
          id: "wwt-alloy",
          parameter: "Aleaciones de acero",
          range: "AISI 304, AISI 316L",
          tolerance: "Composición estándar",
          notes: "Certificados de análisis de material bajo solicitud",
        },
        {
          id: "wwt-fittings",
          parameter: "Conexiones de montaje",
          range: "Roscas NPT, BSP, Brida sanitaria, Varilla central",
          tolerance: "Mecanizado de precisión",
          notes: "Asientos para juntas tóricas o juntas planas",
        },
      ],
      caption: "Valores confirmados de ingeniería. Adaptaciones especiales según necesidades de proceso.",
    },
    features: {
      heading: "Ventajas Operativas y Constructivas",
      intro: "Diseñados para recipientes verticales a presión, sistemas de retrolavado automatizado y procesos severos.",
      items: [
        {
          title: "Cero Migración de Fibras",
          text: "Estructura íntegramente metálica que garantiza la máxima pureza del fluido sin desprendimientos.",
        },
        {
          title: "Excelente Descarga de Torta",
          text: "El perfil en V ensanchado hacia adentro facilita el desprendimiento rápido de la torta por choque de gas o líquido inverso.",
        },
        {
          title: "Elevada Resistencia a la Presión",
          text: "Puntos de unión electrosoldados que soportan sobrepresiones directas y pulsaciones de retrolavado continuo.",
        },
        {
          title: "Montaje Alineado en Placa Portavelas",
          text: "Conectores mecanizados que garantizan una estricta perpendicularidad en baterías densas de elementos.",
        },
      ],
    },
    related: {
      heading: "Productos Cilíndricos Relacionados",
      intro: "Conozca las tuberías de ranura continua o regrese al centro de tubos filtrantes.",
      cards: [
        {
          title: "Tuberías Ranuradas y Colectores Laterales",
          text: "Alambre continuo de perfil en V soldado a varillas de soporte para pozos de captación y distribución uniforme.",
          to: "/ScreenPipesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
            alt: "Tubo de criba de alambre de cuña de acero inoxidable con ranura continua",
          },
          badge: "Pozos y Drenaje",
          linkLabel: "Ver Tuberías Ranuradas",
        },
        {
          title: "Centro de Tubos Filtrantes",
          text: "Cilindros de malla y perfiles de cuña totalmente soldados, diseñados para resistir presiones severas.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Cilindro de tubo filtrante de malla de alambre de acero inoxidable",
          },
          badge: "Categoría Superior",
          linkLabel: "Volver a Tubos Filtrantes",
        },
        {
          title: "Gama de Alambre en Cuña",
          text: "Catálogo completo de paneles planos, rejillas curvadas y cilindros filtrantes para procesos industriales.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Resumen de productos de alambre en cuña",
          },
          badge: "Gama de Productos",
          linkLabel: "Explorar Alambre en Cuña",
        },
      ],
    },
    cta: {
      id: "wedge-wire-tubes-quote",
      eyebrow: "Elementos Bujía a Medida",
      heading: "Solicite Oferta Técnica para Elementos Tipo Bujía",
      lead: "Indique la luz de ranura, diámetro, longitud de vela y tipo de conexión a placa para recibir una oferta técnica a medida.",
      secondaryLabel: "Ver Tubos Filtrantes",
      secondaryTo: "/ScreenTubesPage",
      note: "Informes de prueba de materiales (MTR) y análisis PMI disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "WedgeWireTubes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Filterrohre",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Spaltrohre & Spaltfilterkerzen aus Edelstahl",
      lead: "Vollverschweißte Endlosspalt-Spaltrohre und Filterkerzen für Hochdrucktrennung, Katalysatorrückgewinnung und anspruchsvolle Fest-Flüssig-Filtration in Druckbehältern.",
      secondaryCta: {
        label: "Filterrohre Übersicht",
        to: "/ScreenTubesPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
        alt: "Industrielles Spaltrohr-Filterelement aus Edelstahl für Druckbehälter",
      },
      cardNote: "Hochdruck-Filterkerzen",
      cardLink: {
        label: "Technische Daten",
        to: "/WedgeWireTubesPage#specifications",
      },
      specs: [
        { label: "Spaltöffnungen", value: "0,02 – 5,0 mm" },
        { label: "Werkstoffe", value: "304L / 316L" },
        { label: "Standardlängen", value: "500 – 3.000 mm" },
        { label: "Anschlüsse", value: "Gewinde / Flansch / Schweißende" },
      ],
    },
    overview: {
      eyebrow: "Katalysatorrückgewinnung & Druckfiltration",
      heading: "Vollverschweißte Kerzenkonstruktion für hohe Differenzdrücke",
      paragraphs: [
        "Spaltrohre und Spaltfilterkerzen sind präzise gefertigte zylindrische Filterelemente für stehende Druckbehälter und Kerzenfilter. Kontinuierlich auf Tragstäbe geschweißte V-Drähte garantieren höchste Formstabilität ohne Gewebeauflockerung.",
        "Bei der Katalysatorabtrennung und Feinstfiltration neigen Gewebestrümpfe zu Rissen und Faserabrieb. Spaltfilterkerzen schließen Medienabrieb aus und überstehen intensive Rückspülimpulse verzugsfrei.",
        "Passend für gängige Rohrbodendurchmesser (25 mm bis 150 mm) sowie Sondermaße bis 600 mm, ausgestattet mit Präzisionsgewinden, Bajonett- oder Flanschanschlüssen.",
      ],
      keyPoints: [
        "Vollverschweißter V-Drahtkäfig widersteht hohen Vorwärts- und Rückwärtsdifferenzdrücken ohne Spaltverschiebung.",
        "Sich nach innen erweiternder Spalt verhindert Partikelverkeilung und ermöglicht sparsames, schnelles Rückspülen.",
        "Hergestellt aus AISI 304 und 316L für aggressive Lösungsmittel, Säuren und thermische Regenerierung.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
        alt: "Industrielles Spaltrohr-Filterelement aus Edelstahl für Druckbehälter",
      },
    },
    specTable: {
      heading: "Spezifikationen für Spaltfilterkerzen",
      intro: "Technische Daten für Hochdruck-Kerzenelemente und Spaltrohre. Sondermaße und Anschlüsse werden nach Kundenzeichnung ausgeführt.",
      columns: [
        { key: "parameter", label: "Parameter", align: "left" },
        { key: "range", label: "Fertigungsbereich", align: "left" },
        { key: "tolerance", label: "Toleranz", align: "left" },
        { key: "notes", label: "Konstruktionsdetails", align: "left" },
      ],
      rows: [
        {
          id: "wwt-slot",
          parameter: "Spaltweite",
          range: "0,02 – 5,0 mm",
          tolerance: "±0,015 mm",
          notes: "Gleichmäßige Feinheit über die gesamte Filterfläche",
        },
        {
          id: "wwt-od",
          parameter: "Außendurchmesser (OD)",
          range: "19 – 300 mm",
          tolerance: "±0,3 mm",
          notes: "Typische Kerzendurchmesser: 25, 33, 38, 50, 70 mm",
        },
        {
          id: "wwt-length",
          parameter: "Standardlängen",
          range: "250 – 3.000 mm",
          tolerance: "±1,0 mm",
          notes: "Sonderlängen und Mehrfachsegmente auf Anfrage",
        },
        {
          id: "wwt-alloy",
          parameter: "Edelstahllegierungen",
          range: "AISI 304, AISI 316L",
          tolerance: "Standardanalyse",
          notes: "Prüfzeugnisse auf Wunsch bei Bestellung",
        },
        {
          id: "wwt-fittings",
          parameter: "Anschlussarten",
          range: "Gewinde NPT, BSP, Flansch, Zugstange",
          tolerance: "Präzisionsgedreht",
          notes: "Mit O-Ring-Nut oder Flachdichtungssitz",
        },
      ],
      caption: "Bestätigte Fertigungswerte. Kundenspezifische Auslegungen auf Anfrage.",
    },
    features: {
      heading: "Konstruktions- & Anwendungsvorteile",
      intro: "Konzipiert für vertikale Druckbehälter, automatisierte Rückspülung und anspruchsvolle Suspensionen.",
      items: [
        {
          title: "Keine Faser- oder Medienmigration",
          text: "Die reine Metallkonstruktion garantiert höchste Reinheit ohne Faserabrieb oder Maschenauflösung.",
        },
        {
          title: "Hervorragender Filterkuchenabwurf",
          text: "Die keilförmige Geometrie sorgt für spontanes, vollflächiges Ablösen des Filterkuchens beim Rückstoß.",
        },
        {
          title: "Hohe Druckstoßfestigkeit",
          text: "Widerstandsgeschweißte Knotenpunkte widerstehen Druckschlägen und dauerhaften Pulsationszyklen.",
        },
        {
          title: "Exakte Ausrichtung im Rohrboden",
          text: "CNC-gedrehte Anschlussstücke garantieren parallele Flucht im dichten Kerzenfeld.",
        },
      ],
    },
    related: {
      heading: "Passende Zylindrische Spaltsiebe",
      intro: "Vergleichen Sie Spaltfilterkerzen mit Brunnenfilterrohren oder kehren Sie zur Kategorie Filterrohre zurück.",
      cards: [
        {
          title: "Filterrohre & Sternverteilersysteme",
          text: "Kontinuierlicher V-Profildraht auf Längstragstäben für Tiefbrunnen, Harzfallen und gleichmäßige Medienverteilung.",
          to: "/ScreenPipesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
            alt: "Spaltrohr aus Edelstahl mit kontinuierlicher Spaltweite und Längstragstäben",
          },
          badge: "Brunnen & Verteilung",
          linkLabel: "Filterrohre ansehen",
        },
        {
          title: "Kategorie Filterrohre",
          text: "Präzisionsgefertigte Maschenzylinder und Spaltrohre aus Edelstahl für anspruchsvolle Filtrationsaufgaben.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Edelstahl-Drahtgewebe-Filterrohrzylinder",
          },
          badge: "Übergeordnete Kategorie",
          linkLabel: "Zurück zu Filterrohre",
        },
        {
          title: "Spaltsiebe Gesamtübersicht",
          text: "Flachsiebe, Bogensiebe, Zylinder und Düsen für anspruchsvolle industrielle Trennprozesse.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Übersicht Edelstahl-Spaltsiebe",
          },
          badge: "Produktfamilie",
          linkLabel: "Spaltsiebe entdecken",
        },
      ],
    },
    cta: {
      id: "wedge-wire-tubes-quote",
      eyebrow: "Maßgefertigte Spaltfilterkerzen",
      heading: "Technisches Angebot für Spaltfilterkerzen anfordern",
      lead: "Nennen Sie uns Spaltweite, Kerzendurchmesser, Nutzlänge und Anschlussart für ein maßgeschneidertes Angebot.",
      secondaryLabel: "Filterrohre anzeigen",
      secondaryTo: "/ScreenTubesPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Analysen bei entsprechender Bestellangabe verfügbar.",
    },
  },
  fr: {
    breadcrumbKey: "WedgeWireTubes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Tubes Filtrants",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tubes à Fil en Coin et Bougies Filtrantes",
      lead: "Tubes à fente continue et éléments bougies soudés conçus pour la séparation haute pression, la récupération de catalyseur et la filtration solide-liquide sévère en cuves sous pression.",
      secondaryCta: {
        label: "Catégorie Tubes Filtrants",
        to: "/ScreenTubesPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
        alt: "Élément filtrant en fil en coin en acier inoxydable pour cuves sous pression",
      },
      cardNote: "Bougies Haute Pression",
      cardLink: {
        label: "Spécifications",
        to: "/WedgeWireTubesPage#specifications",
      },
      specs: [
        { label: "Ouvertures de fente", value: "0,02 – 5,0 mm" },
        { label: "Alliages", value: "304L / 316L" },
        { label: "Longueurs standards", value: "500 – 3 000 mm" },
        { label: "Raccordements", value: "Filetage / Bride / Soudure" },
      ],
    },
    overview: {
      eyebrow: "Récupération de Catalyseurs et Filtration Sous Pression",
      heading: "Conception Mécano-Soudée Rigide pour Hautes Pressions Différentielles",
      paragraphs: [
        "Les tubes en fil profilé et bougies filtrantes sont des éléments cylindriques conçus pour les filtres à bougies verticaux. Réalisés par soudure continue d'un fil en V sur des barres axiales, ils présentent une grande rigidité sans dégradation interne.",
        "Dans la récupération de catalyseurs et la clarification chimique, les toiles ou manchons textiles risquent la déchirure et le relargage de fibres. Les bougies en fil profilé éliminent tout risque de contamination et supportent les chocs de contre-lavage répétés sans déformation.",
        "Fabriquées aux diamètres standard des plaques tubulaires (25 mm à 150 mm) ou sur mesure jusqu'à 600 mm, elles reçoivent des embouts filetés (BSP/NPT), raccords baïonnette ou brides étanches usinées dans la masse.",
      ],
      keyPoints: [
        "Structure mécano-soudée rigide résistant aux pressions différentielles directes et inverses sans altération de fente.",
        "Ouverture élargie vers l'intérieur empêchant l'incrustation et facilitant le décrochage rapide du gâteau de filtration.",
        "Fabrication en acier inoxydable AISI 304 et 316L adaptée aux solvants agressifs et à la régénération thermique.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
        alt: "Élément filtrant en fil en coin en acier inoxydable pour cuves sous pression",
      },
    },
    specTable: {
      heading: "Spécifications des Bougies et Tubes Filtrants",
      intro: "Normes dimensionnelles pour bougies filtrantes et tubes sous pression. Raccordements spécifiques réalisés selon vos tolérances.",
      columns: [
        { key: "parameter", label: "Paramètre", align: "left" },
        { key: "range", label: "Plage Standard", align: "left" },
        { key: "tolerance", label: "Tolérance", align: "left" },
        { key: "notes", label: "Détails Techniques", align: "left" },
      ],
      rows: [
        {
          id: "wwt-slot",
          parameter: "Ouverture de fente",
          range: "0,02 – 5,0 mm",
          tolerance: "±0,015 mm",
          notes: "Régularité stricte sur toute la surface utile",
        },
        {
          id: "wwt-od",
          parameter: "Diamètre extérieur (OD)",
          range: "19 – 300 mm",
          tolerance: "±0,3 mm",
          notes: "Diamètres courants de bougies : 25, 33, 38, 50, 70 mm",
        },
        {
          id: "wwt-length",
          parameter: "Longueur standard",
          range: "250 – 3 000 mm",
          tolerance: "±1,0 mm",
          notes: "Longueurs spéciales assemblées selon cahier des charges",
        },
        {
          id: "wwt-alloy",
          parameter: "Nuances d'acier",
          range: "AISI 304, AISI 316L",
          tolerance: "Composition conforme",
          notes: "Certificats matière disponibles sur demande",
        },
        {
          id: "wwt-fittings",
          parameter: "Raccordements de fixation",
          range: "Filetages NPT, BSP, Bride sanitaire, Tirant",
          tolerance: "Usinage de précision",
          notes: "Portées pour joints toriques ou joints plats",
        },
      ],
      caption: "Données de fabrication certifiées. Adaptations spéciales sur mesure.",
    },
    features: {
      heading: "Performances et Avantages Industriels",
      intro: "Développées pour filtres verticaux automatiques, rétro-lavage gaz ou liquide et fluides chargés.",
      items: [
        {
          title: "Zéro Relargage de Fibres",
          text: "Structure 100 % métallique garantissant une pureté absolue du filtrat sans risque d'effilochage.",
        },
        {
          title: "Décolmatage Instantané du Gâteau",
          text: "L'élargissement intérieur libère le gâteau sans adhérence lors des chocs de décolmatage gazeux ou liquide.",
        },
        {
          title: "Résistance aux Pressions Alternées",
          text: "Les soudures de fusion absorbent les fortes montées de pression et les cycles de contre-pression répétés.",
        },
        {
          title: "Alignement Parfait sur Plaque",
          text: "Embouts tournés assurant un parallélisme strict au sein des faisceaux denses de bougies.",
        },
      ],
    },
    related: {
      heading: "Produits Complémentaires en Fil Profilé",
      intro: "Consultez les tubes crépines pour forages ou revenez au pôle tubes filtrants.",
      cards: [
        {
          title: "Tubes Crépines et Collecteurs Latéraux",
          text: "Fil continu à profil en V soudé par fusion sur des tiges longitudinales pour puits d'eau et distribution uniforme.",
          to: "/ScreenPipesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
            alt: "Tube crépine en acier inoxydable à fente continue et tiges de support",
          },
          badge: "Forage & Collecteurs",
          linkLabel: "Voir les Tubes Crépines",
        },
        {
          title: "Catégorie Tubes Filtrants",
          text: "Cylindres en toile métallique et fil en coin conçus pour filtrations exigeantes et forte résistance à l'écrasement.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Tube filtrant cylindrique en toile métallique d'acier inoxydable",
          },
          badge: "Catégorie Parente",
          linkLabel: "Retour aux Tubes Filtrants",
        },
        {
          title: "Gamme Fil en Coin",
          text: "Panneaux plans, grilles courbes et filtres cylindriques pour procédés industriels de séparation.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Vue générale des produits en fil en coin",
          },
          badge: "Famille de Produits",
          linkLabel: "Explorer le Fil en Coin",
        },
      ],
    },
    cta: {
      id: "wedge-wire-tubes-quote",
      eyebrow: "Bougies Filtrantes sur Mesure",
      heading: "Demandez une Offre pour Bougies Filtrantes",
      lead: "Précisez la fente, le diamètre, la longueur de bougie et le mode de raccordement pour obtenir une proposition détaillée.",
      secondaryLabel: "Voir les Tubes Filtrants",
      secondaryTo: "/ScreenTubesPage",
      note: "Certificats matière (MTR) et contrôles spectrométriques PMI disponibles sur demande à la commande.",
    },
  },
  pt: {
    breadcrumbKey: "WedgeWireTubes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Tubos de Filtragem",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tubos de Arame em Cunha e Elementos Filtrantes Tipo Vela",
      lead: "Tubos de tela perfilada e elementos filtrantes tipo vela totalmente soldados para separação sob alta pressão, recuperação de catalisadores e filtração rigorosa em vasos pressurizados.",
      secondaryCta: {
        label: "Categoria Tubos de Filtragem",
        to: "/ScreenTubesPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
        alt: "Elemento filtrante de tubo de tela tipo cunha em aço inoxidável para vasos pressurizados",
      },
      cardNote: "Velas para Vasos Pressurizados",
      cardLink: {
        label: "Especificações",
        to: "/WedgeWireTubesPage#specifications",
      },
      specs: [
        { label: "Abertura de ranhura", value: "0,02 – 5,0 mm" },
        { label: "Ligas", value: "304L / 316L" },
        { label: "Comprimentos", value: "500 – 3.000 mm" },
        { label: "Terminações", value: "Rosca / Flange / Solda" },
      ],
    },
    overview: {
      eyebrow: "Recuperação de Catalisadores & Filtração Pressurizada",
      heading: "Construção Eletrosoldada para Altas Pressões Diferenciais",
      paragraphs: [
        "Os tubos e elementos tipo vela em arame cunha são filtros cilíndricos de alta precisão concebidos para filtros vela em vasos verticais fechados. Compostos por fios contínuos em V soldados por fusão sobre barras longitudinais de apoio, garantem rigidez permanente sem desgaste de núcleo.",
        "Na recuperação de catalisadores nobres e retenção de carvão ativado, mangas de tecido ou malhas simples sofrem rasgos e desprendimento de fibras. Os elementos vela de tela perfilada suportam choques severos de retrolavagem sem qualquer empenamento.",
        "Compatíveis com as furações convencionais de espelhos de vasos (comumente 25 mm a 150 mm) e dimensões sob encomenda até 600 mm, incluem conexões roscadas usinadas (BSP/NPT), engates rápidos ou flanges torneadas para vedação estanque.",
      ],
      keyPoints: [
        "Estrutura rígida de arame em V que suporta pressões diferenciais diretas e reversas sem deslocamento de fenda.",
        "Abertura interna com alívio progressivo que impede aprisionamento de partículas e agiliza o descarte da torta.",
        "Construção em AISI 304 e 316L para total resistência a solventes agressivos e esterilização a vapor.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
        alt: "Elemento filtrante de tubo de tela tipo cunha em aço inoxidável para vasos pressurizados",
      },
    },
    specTable: {
      heading: "Especificações Técnicas de Elementos Tipo Vela",
      intro: "Parâmetros técnicos para elementos vela em vasos pressurizados. Terminações e comprimentos especiais executados conforme desenho.",
      columns: [
        { key: "parameter", label: "Parâmetro", align: "left" },
        { key: "range", label: "Faixa Padrão", align: "left" },
        { key: "tolerance", label: "Tolerância", align: "left" },
        { key: "notes", label: "Detalhes Construtivos", align: "left" },
      ],
      rows: [
        {
          id: "wwt-slot",
          parameter: "Abertura de ranhura",
          range: "0,02 – 5,0 mm",
          tolerance: "±0,015 mm",
          notes: "Homogeneidade dimensional em toda a superfície filtrante",
        },
        {
          id: "wwt-od",
          parameter: "Diâmetro externo (OD)",
          range: "19 – 300 mm",
          tolerance: "±0,3 mm",
          notes: "Diâmetros comuns de vela: 25, 33, 38, 50, 70 mm",
        },
        {
          id: "wwt-length",
          parameter: "Comprimento padrão",
          range: "250 – 3.000 mm",
          tolerance: "±1,0 mm",
          notes: "Comprimentos sob medida conforme profundidade do vaso",
        },
        {
          id: "wwt-alloy",
          parameter: "Ligas de aço inox",
          range: "AISI 304, AISI 316L",
          tolerance: "Composição padrão",
          notes: "Certificados de ensaio de material sob encomenda",
        },
        {
          id: "wwt-fittings",
          parameter: "Conexões de montagem",
          range: "Roscas NPT, BSP, Flange sanitária, Tirante",
          tolerance: "Usinagem de precisão",
          notes: "Alojamentos para anéis O-ring ou juntas planas",
        },
      ],
      caption: "Faixas dimensionais confirmadas. Projetos especiais fornecidos sob especificação.",
    },
    features: {
      heading: "Diferenciais Técnicos e Operação",
      intro: "Projetados para vasos verticais automatizados, ciclos de retrolavagem rápida e líquidos com sólidos agressivos.",
      items: [
        {
          title: "Zero Desprendimento de Partículas",
          text: "Construção puramente metálica que garante pureza total sem perda de fibras ou desfiamento.",
        },
        {
          title: "Desprendimento Ágil da Torta",
          text: "O alívio interno da fenda em V expulsa a torta acumulada de forma uniforme durante pulsos reversos de gás ou líquido.",
        },
        {
          title: "Alta Resistência a Pulsos Reversos",
          text: "Pontos de fusão contínuos que suportam as variações violentas de pressão dos ciclos de limpeza.",
        },
        {
          title: "Perfeita Montagem no Espelho",
          text: "Terminações usinadas com precisão garantindo perpendicularidade e paralelismo entre as velas do feixe.",
        },
      ],
    },
    related: {
      heading: "Produtos Cilíndricos Complementares",
      intro: "Conheça os tubos de peneira de poço ou retorne à categoria geral de tubos de filtragem.",
      cards: [
        {
          title: "Tubos de Peneira e Coletores Laterais",
          text: "Arame contínuo de perfil em V soldado a hastes longitudinais para poços profundos, drenagem e distribuição uniforme de fluidos.",
          to: "/ScreenPipesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
            alt: "Tubo de peneira de arame em cunha de aço inoxidável com ranhura contínua e hastes de suporte",
          },
          badge: "Poços e Drenos",
          linkLabel: "Ver Tubos de Peneira",
        },
        {
          title: "Categoria Tubos de Filtragem",
          text: "Cilindros de tela metálica e arame em cunha sob medida para condições exigentes e alta resistência ao colapso.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Cilindro de tubo filtrante de malha de aço inoxidável",
          },
          badge: "Categoria Mão",
          linkLabel: "Voltar para Tubos de Filtragem",
        },
        {
          title: "Linha de Arame em Cunha",
          text: "Visão ampla de painéis planos, telas curvas e cilindros filtrantes para a indústria de processo.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Visão geral de produtos de arame em cunha",
          },
          badge: "Família de Produtos",
          linkLabel: "Explorar Arame em Cunha",
        },
      ],
    },
    cta: {
      id: "wedge-wire-tubes-quote",
      eyebrow: "Elementos Vela Sob Medida",
      heading: "Solicite uma Cotação para Elementos Vela",
      lead: "Informe a abertura de fenda, diâmetro externo, comprimento útil e conexões para receber uma proposta completa.",
      secondaryLabel: "Ver Tubos de Filtragem",
      secondaryTo: "/ScreenTubesPage",
      note: "Relatórios de teste de material (MTR) e ensaio PMI disponíveis quando solicitados no pedido.",
    },
  },
};
